/**
 * Display formatting for CPCel. Bangladesh conventions throughout.
 *
 * Formatting NEVER performs arithmetic — it takes an already-correct decimal
 * string and renders it. Keeping the two apart is what stops a rounding
 * decision from being made accidentally in a JSX expression.
 */
import { money, round, type MoneyInput } from '@/lib/money';

export const CURRENCY_SYMBOL = '৳';

/**
 * Bangladeshi digit grouping: the last three digits, then pairs.
 * 300000 -> 3,00,000
 */
function groupBangladeshi(integerDigits: string): string {
  if (integerDigits.length <= 3) return integerDigits;
  const last3 = integerDigits.slice(-3);
  const rest = integerDigits.slice(0, -3);
  const grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${grouped},${last3}`;
}

export type CurrencyOptions = {
  /** Show the ৳ sign. Default true. */
  symbol?: boolean;
  /** Always show 2 decimals. Default false — whole amounts render without them. */
  decimals?: boolean;
  /** Render as `—` when the value is exactly zero. */
  dashOnZero?: boolean;
  /** Prefix positive values with `+`. Used for cash differences. */
  signed?: boolean;
};

/** `৳85,000` / `৳1,23,456.50` */
export function formatCurrency(value: MoneyInput, options: CurrencyOptions = {}): string {
  const { symbol = true, decimals = false, dashOnZero = false, signed = false } = options;
  const amount = round(value);

  if (dashOnZero && amount.isZero()) return '—';

  const negative = amount.isNegative();
  const fixed = amount.abs().toFixed(2);
  const [intPartRaw = '0', fracPart = '00'] = fixed.split('.');
  const intPart = groupBangladeshi(intPartRaw);

  const body = decimals || fracPart !== '00' ? `${intPart}.${fracPart}` : intPart;
  const sign = negative ? '-' : signed && !amount.isZero() ? '+' : '';

  return `${sign}${symbol ? CURRENCY_SYMBOL : ''}${body}`;
}

/** Compact form for dense KPI tiles: `৳85.0K`, `৳1.2L`, `৳3.4Cr`. */
export function formatCurrencyCompact(value: MoneyInput): string {
  const amount = round(value);
  const abs = amount.abs();
  const sign = amount.isNegative() ? '-' : '';

  if (abs.greaterThanOrEqualTo(10_000_000)) {
    return `${sign}${CURRENCY_SYMBOL}${abs.dividedBy(10_000_000).toDecimalPlaces(2).toString()}Cr`;
  }
  if (abs.greaterThanOrEqualTo(100_000)) {
    return `${sign}${CURRENCY_SYMBOL}${abs.dividedBy(100_000).toDecimalPlaces(2).toString()}L`;
  }
  if (abs.greaterThanOrEqualTo(1_000)) {
    return `${sign}${CURRENCY_SYMBOL}${abs.dividedBy(1_000).toDecimalPlaces(1).toString()}K`;
  }
  return formatCurrency(amount);
}

/** `70%` or `70.5%` */
export function formatPercent(value: MoneyInput, decimals = 0): string {
  return `${round(value, decimals).toFixed(decimals)}%`;
}

/** `1,250` — plain integer counts, grouped the Bangladeshi way. */
export function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '0';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0';
  const negative = n < 0;
  const grouped = groupBangladeshi(Math.abs(Math.trunc(n)).toString());
  return `${negative ? '-' : ''}${grouped}`;
}

export function formatQuantity(value: number, unitLabel?: string): string {
  const base = formatNumber(value);
  if (!unitLabel) return base;
  return `${base} ${unitLabel}${Math.abs(value) === 1 ? '' : 's'}`;
}

/**
 * Bangladesh mobile numbers. Accepts `01712345678`, `+8801712345678`,
 * `8801712345678` and renders the canonical local form `01712-345678`.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  const local = digits.startsWith('880') ? `0${digits.slice(3)}` : digits;
  if (local.length === 11 && local.startsWith('01')) {
    return `${local.slice(0, 5)}-${local.slice(5)}`;
  }
  return phone;
}

/** Normalise any accepted BD mobile format to the stored form `01XXXXXXXXX`. */
export function normalisePhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('880')) return `0${digits.slice(3)}`;
  if (digits.startsWith('0')) return digits;
  if (digits.length === 10 && digits.startsWith('1')) return `0${digits}`;
  return digits;
}

export function isValidBdMobile(phone: string): boolean {
  return /^01[3-9]\d{8}$/.test(normalisePhone(phone));
}

/** Bytes as `1.4 MB`. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Title-case an ENUM_LIKE_VALUE for display: `SALE_RETURN` -> `Sale Return`. */
export function humaniseEnum(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Initials for an avatar, e.g. `Abdur Rahim` -> `AR`. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  return `${(parts[0] ?? '').charAt(0)}${(parts[parts.length - 1] ?? '').charAt(0)}`.toUpperCase();
}

/**
 * Direction-aware description of a cash difference, used so that colour is
 * never the only signal (WCAG 1.4.1).
 */
export function describeCashDifference(difference: MoneyInput): { label: string; tone: 'ok' | 'short' | 'over' } {
  const d = money(difference);
  if (d.isZero()) return { label: 'Cash matches', tone: 'ok' };
  if (d.isNegative()) return { label: `Short by ${formatCurrency(d.abs())}`, tone: 'short' };
  return { label: `Over by ${formatCurrency(d)}`, tone: 'over' };
}
